import { useState, useEffect, useRef, useCallback } from 'react';

// Touch-driven pull-to-refresh for the window scroller.
//
// The feed scrolls on `window` (no custom scroll container), so this binds at
// the document level and only claims a gesture that starts at the very top.
//
// Two details do most of the work here:
//  1. Axis lock. The hero carousel is horizontally scrollable and sits at the
//     top of the feed — exactly where this gesture arms. Without cancelling on
//     horizontal intent, swiping the carousel also drags the feed down.
//  2. `passive: false` on touchmove. Claiming the gesture requires
//     preventDefault() to suppress the native WKWebView rubber-band, which
//     would otherwise fight the transform for the same finger.

const THRESHOLD = 64;    // px of travel that arms a refresh
const MAX_PULL = 96;     // asymptotic ceiling on travel
const HOLD = 56;         // resting offset while the request is in flight
const SLOP = 8;          // px of movement before the axis is decided
const MIN_SPIN = 450;    // ms the spinner stays up even if the RPC returns instantly

export function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Kept in refs so the listeners bind once and never restack mid-gesture.
  const onRefreshRef = useRef(onRefresh);
  const startY = useRef(0);
  const startX = useRef(0);
  const tracking = useRef(false);  // touch began at the top, axis undecided
  const claimed = useRef(false);   // axis resolved as a vertical pull
  const pullRef = useRef(0);
  const busy = useRef(false);

  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  const setPullBoth = useCallback((v) => {
    pullRef.current = v;
    setPull(v);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Resized to desktop mid-gesture — drop any travel so the content
      // doesn't stay translated.
      tracking.current = false;
      claimed.current = false;
      setDragging(false);
      setPullBoth(0);
      return;
    }

    const onTouchStart = (e) => {
      if (busy.current || e.touches.length !== 1) return;
      // A modal is open over the feed. This listener is on `document`, so
      // without this a drag inside the sheet would pull the feed behind it.
      // Set by useModalDismiss.
      if (document.body.dataset.modalOpen) return;
      // scrollY can read negative mid rubber-band on iOS; <= 0 is "at the top".
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      tracking.current = true;
      claimed.current = false;
    };

    const onTouchMove = (e) => {
      if (!tracking.current) return;
      const dy = e.touches[0].clientY - startY.current;
      const dx = e.touches[0].clientX - startX.current;

      if (!claimed.current) {
        // Horizontal intent (the carousel) or an upward drag — let the page have it.
        if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
          if (Math.abs(dx) > SLOP || dy < -SLOP) tracking.current = false;
          return;
        }
        if (dy < SLOP) return;
        claimed.current = true;
        setDragging(true);
      }

      // The page may have scrolled under us between events.
      if (window.scrollY > 0) {
        tracking.current = false;
        claimed.current = false;
        setDragging(false);
        setPullBoth(0);
        return;
      }

      e.preventDefault();
      // Exponential resistance: 1:1 at the start, asymptotic toward MAX_PULL.
      const raw = dy - SLOP;
      setPullBoth(MAX_PULL * (1 - Math.exp(-raw / MAX_PULL)));
    };

    const finish = async () => {
      if (!tracking.current) return;
      const armed = claimed.current && pullRef.current >= THRESHOLD;
      tracking.current = false;
      claimed.current = false;
      setDragging(false);

      if (!armed) {
        setPullBoth(0);
        return;
      }

      busy.current = true;
      setRefreshing(true);
      setPullBoth(HOLD);
      const startedAt = Date.now();
      try {
        await onRefreshRef.current?.();
      } catch (err) {
        console.error('pull-to-refresh:', err);
      } finally {
        // A cached RPC can return in ~40ms; without a floor the spinner just
        // flickers and reads as "nothing happened".
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_SPIN) {
          await new Promise((r) => setTimeout(r, MIN_SPIN - elapsed));
        }
        setRefreshing(false);
        setPullBoth(0);
        busy.current = false;
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', finish, { passive: true });
    document.addEventListener('touchcancel', finish, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', finish);
      document.removeEventListener('touchcancel', finish);
    };
  }, [enabled, setPullBoth]);

  return {
    pull,
    refreshing,
    dragging,
    armed: pull >= THRESHOLD,
    progress: Math.min(1, pull / THRESHOLD),
  };
}
